import React, { Fragment, useState, useEffect } from "react";
import Breadcrumb from "../../common/breadcrumb";
import MenuItem from "@mui/material/MenuItem";
import { Card, CardBody, Container, Row } from "reactstrap";
import { Button, Chip } from "@mui/material";
import { Grid, TextField } from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import { PersonSearch, Add, Refresh, Send, SimCardDownloadRounded } from "@mui/icons-material";
import { makeStyles } from "@mui/styles";
import CommonAccordion from "../../common/commonAccordion";
import CommonTableApBulkInvite from "../../common/commonTableApBulkInvite";
import {
  getAllBulkInviteList,
  searchBulkInviteList,
  resendBulkInvite,
  getBulkInviteStatistics,
  exportToExcelBulkInviteList,
} from "../../api/ap_services";
import CommonLoader from "../../common/commonLoader";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import {
  vendorBulkInviteListTableHeader,
  vendorBulkInviteListTableData,
} from "../../common/commonConstantAp";

const useStyles = makeStyles((theme) => ({
  btnClass: {
    padding: "0.5rem 1.5rem!important",
    fontWeight: 600,
  },
  btnJust: {
    display: "flex!important",
    justifyContent: "end!important",
  },
  cardbody: {
    padding: `${theme.spacing(1.8, 1.3)} !important`,
    [theme.breakpoints.down("sm")]: {
      padding: `${theme.spacing(0)} !important`,
      margin: `${theme.spacing(0, 1)} !important`,
    },
  },
  muiGrid: {
    marginTop: "-2vh",
    [theme.breakpoints.down("sm")]: {
      lineHeight: 5,
    },
  },
}));

const VendorBulkInviteList = () => {
  const history = useNavigate();
  const classes = useStyles();
  const [totalRecord, setTotalRecord] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statistics, setStatistics] = useState({
    totalVendorsWithCode: 0,
    inviteSent: 0,
    inviteFailed: 0,
    processing: 0,
    completed: 0,
  });

  const filterInitialValues = {
    searchVendorCode: "",
    searchVendorName: "",
    searchInviteStatus: "",
  };
  const [searchData, setSearchData] = useState(filterInitialValues);

  useEffect(() => {
    loadListData();
    loadStatistics();
  }, []);

  const loadListData = () => {
    setLoading(true);
    getAllBulkInviteList(page, rowsPerPage).then((res) => {
      setLoading(false);
      const raw = res.response.responseObject ?? [];
      setData(Array.isArray(raw) ? raw : []);
      setTotalRecord(res.response.responseMap?.totalCount ?? 0);
    });
  };

  const loadStatistics = () => {
    getBulkInviteStatistics().then((res) => {
      console.log("Statistics response:", res);
      if (res.response && res.response.responseObject) {
        const stats = res.response.responseObject;
        console.log("Setting statistics:", stats);
        setStatistics({
          totalVendorsWithCode: stats.totalVendorsWithCode || 0,
          inviteSent: stats.inviteSent || 0,
          inviteFailed: stats.inviteFailed || 0,
          processing: stats.processing || 0,
          completed: stats.completed || 0,
        });
      }
    }).catch((err) => {
      console.error("Error loading statistics:", err);
    });
  };

  const onFilterInputChange = (event) => {
    const { name, value } = event.target;
    setSearchData((prev) => ({ ...prev, [name]: value }));
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    getAllBulkInviteList(newPage, rowsPerPage).then((res) => {
      const raw = res.response.responseObject ?? [];
      setData(Array.isArray(raw) ? raw : []);
      setTotalRecord(res.response.responseMap?.totalCount ?? 0);
    });
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(event.target.value);
    setPage(0);
    getAllBulkInviteList(0, event.target.value).then((res) => {
      const raw = res.response.responseObject ?? [];
      setData(Array.isArray(raw) ? raw : []);
      setTotalRecord(res.response.responseMap?.totalCount ?? 0);
    });
  };

  const filterData = () => {
    setLoading(true);
    searchBulkInviteList(searchData, page, rowsPerPage).then((res) => {
      setLoading(false);
      const raw = res?.response?.responseObject ?? [];
      setData(Array.isArray(raw) ? raw : []);
      setTotalRecord(res?.response?.responseMap?.totalCount ?? 0);
    });
  };

  const handleExportBulkInviteList = () => {
    setLoading(true);
    const dateTime = "Bulk_Invite_List".concat(
      "_",
      moment(new Date()).format("DD_MM_YYYY_HH_mm_SS"),
    );
    exportToExcelBulkInviteList(searchData, dateTime)
      .then(() => {
        toast.success("Excel downloaded successfully");
      })
      .catch(() => {
        toast.error("Failed to download Excel");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleResend = (row) => {
    if (window.confirm("Are you sure you want to resend the invite?")) {
      setLoading(true);
      resendBulkInvite(row.vendorBulkInviteId || row.vendor_bulk_invite_id || row.VENDOR_BULK_INVITE_ID)
        .then((res) => {
          setLoading(false);
          if (res.response.responseCode) {
            toast.success("Invite resent successfully");
            loadListData();
            loadStatistics();
          } else {
            toast.error(res.response.message || "Failed to resend invite");
          }
        })
        .catch((err) => {
          setLoading(false);
          toast.error("Error resending invite");
        });
    }
  };

  const getStatusChip = (status) => {
    const statusColors = {
      INVITE_SENT: "success",
      INVITE_FAILED: "error",
      PROCESSING: "warning",
      COMPLETED: "primary",
    };
    return (
      <Chip
        label={status || "N/A"}
        color={statusColors[status] || "default"}
        size="small"
      />
    );
  };

  const tableColumns = [
    {
      name: "vendorCode",
      label: "Vendor Code",
      width: "100px",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return row.vendorCode || row.vendor_code || row.VENDOR_CODE || "-";
        },
      },
    },
    {
      name: "vendorName",
      label: "Vendor Name",
      width: "180px",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return row.vendorName || row.vendor_name || row.VENDOR_NAME || "-";
        },
      },
    },
    {
      name: "brand",
      label: "Brand",
      width: "120px",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return row.brand || row.BRAND || "-";
        },
      },
    },
    {
      name: "vendorType",
      label: "Vendor Type",
      width: "120px",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return row.vendorType || row.vendor_type || row.VENDOR_TYPE || "-";
        },
      },
    },
    {
      name: "vendorClassification",
      label: "Vendor Classification",
      width: "150px",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return row.vendorClassification || row.vendor_classification || row.VENDOR_CLASSIFICATION || "-";
        },
      },
    },
    {
      name: "inviteStatus",
      label: "Invite Status",
      width: "130px",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          const status = row.inviteStatus || row.invite_status || row.INVITE_STATUS;
          return getStatusChip(status);
        },
      },
    },
    {
      name: "ownerEmail",
      label: "Owner Mail ID",
      width: "200px",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return row.ownerEmail || row.owner_email || row.OWNER_EMAIL || "-";
        },
      },
    },
    {
      name: "ownerContactNo",
      label: "Owner Contact No",
      width: "140px",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return row.ownerContactNo || row.owner_contact_no || row.OWNER_CONTACT_NO || "-";
        },
      },
    },
    {
      name: "primaryEmail",
      label: "Primary Mail ID",
      width: "200px",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return row.primaryEmail || row.primary_email || row.PRIMARY_EMAIL || "-";
        },
      },
    },
    {
      name: "primaryContact",
      label: "Primary Contact",
      width: "140px",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return row.primaryContact || row.primary_contact || row.PRIMARY_CONTACT || "-";
        },
      },
    },
    {
      name: "vendorConfirmDate",
      label: "Vendor Confirm Date",
      width: "160px",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          const date = row.vendorConfirmDate || row.vendor_confirm_date || row.VENDOR_CONFIRM_DATE;
          return date ? moment(date).format("DD-MM-YYYY HH:mm") : "-";
        },
      },
    },
    {
      name: "actions",
      label: "Actions",
      width: "120px",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          const status = row.inviteStatus || row.invite_status || row.INVITE_STATUS;
          const id = row.vendorBulkInviteId || row.vendor_bulk_invite_id || row.VENDOR_BULK_INVITE_ID;
          if (status === "INVITE_FAILED") {
            return (
              <Button
                variant="contained"
                color="secondary"
                size="small"
                startIcon={<Send />}
                onClick={() => handleResend(row)}
              >
                Resend
              </Button>
            );
          }
          return "-";
        },
      },
    },
  ];

  return (
    <Fragment>
      {loading ? <CommonLoader /> : null}
      <Breadcrumb
        title="Send Bulk Invite"
        parent={
          <div className={classes.btnJust}>
            <Button
              color={"secondary"}
              variant="contained"
              type="button"
              className={`${classes.btnClass}`}
              startIcon={<Add />}
              onClick={() =>
                history(`${process.env.PUBLIC_URL}/master/create-bulk-invite`)
              }
            >
              Send Bulk Invite
            </Button>
          </div>
        }
      />
      <Container fluid={true}>
        <Row>
          <Card style={{ marginBottom: "5px", width: "100%" }}>
            <CardBody>
              <h5 style={{ marginBottom: "15px", fontWeight: "600" }}>Statistics</h5>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={2.4}>
                  <div style={{ textAlign: "center", padding: "15px", backgroundColor: "#f5f5f5", borderRadius: "8px" }}>
                    <div style={{ fontSize: "24px", fontWeight: "bold", color: "#1976d2" }}>
                      {statistics.totalVendorsWithCode}
                    </div>
                    <div style={{ fontSize: "12px", color: "#666", marginTop: "5px" }}>
                      Total Vendors
                    </div>
                  </div>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                  <div style={{ textAlign: "center", padding: "15px", backgroundColor: "#e8f5e9", borderRadius: "8px" }}>
                    <div style={{ fontSize: "24px", fontWeight: "bold", color: "#2e7d32" }}>
                      {statistics.inviteSent}
                    </div>
                    <div style={{ fontSize: "12px", color: "#666", marginTop: "5px" }}>
                      INVITE_SENT
                    </div>
                  </div>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                  <div style={{ textAlign: "center", padding: "15px", backgroundColor: "#ffebee", borderRadius: "8px" }}>
                    <div style={{ fontSize: "24px", fontWeight: "bold", color: "#c62828" }}>
                      {statistics.inviteFailed}
                    </div>
                    <div style={{ fontSize: "12px", color: "#666", marginTop: "5px" }}>
                      INVITE_FAILED
                    </div>
                  </div>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                  <div style={{ textAlign: "center", padding: "15px", backgroundColor: "#fff3e0", borderRadius: "8px" }}>
                    <div style={{ fontSize: "24px", fontWeight: "bold", color: "#ef6c00" }}>
                      {statistics.processing}
                    </div>
                    <div style={{ fontSize: "12px", color: "#666", marginTop: "5px" }}>
                      PROCESSING
                    </div>
                  </div>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                  <div style={{ textAlign: "center", padding: "15px", backgroundColor: "#e3f2fd", borderRadius: "8px" }}>
                    <div style={{ fontSize: "24px", fontWeight: "bold", color: "#1565c0" }}>
                      {statistics.completed}
                    </div>
                    <div style={{ fontSize: "12px", color: "#666", marginTop: "5px" }}>
                      COMPLETED
                    </div>
                  </div>
                </Grid>
              </Grid>
            </CardBody>
          </Card>
          <CommonAccordion accordionTitle={"Advanced Search"}>
            <Grid container columnSpacing={2} className={classes.muiGrid}>
              <Grid item sm={3} xs={12}>
                <TextField
                  fullWidth
                  label="Vendor Code"
                  size="small"
                  name="searchVendorCode"
                  value={searchData.searchVendorCode}
                  onChange={onFilterInputChange}
                  placeholder="Enter Vendor Code"
                />
              </Grid>
              <Grid item sm={3} xs={12}>
                <TextField
                  fullWidth
                  label="Vendor Name"
                  size="small"
                  name="searchVendorName"
                  value={searchData.searchVendorName}
                  onChange={onFilterInputChange}
                  placeholder="Enter Vendor Name"
                />
              </Grid>
              <Grid item sm={3} xs={12}>
                <TextField
                  fullWidth
                  label="Invite Status"
                  size="small"
                  select
                  name="searchInviteStatus"
                  value={searchData.searchInviteStatus}
                  onChange={onFilterInputChange}
                >
                  <MenuItem value="">
                    <em>All Status</em>
                  </MenuItem>
                  <MenuItem value="INVITE_SENT">INVITE_SENT</MenuItem>
                  <MenuItem value="INVITE_FAILED">INVITE_FAILED</MenuItem>
                  <MenuItem value="PROCESSING">PROCESSING</MenuItem>
                  <MenuItem value="COMPLETED">COMPLETED</MenuItem>
                </TextField>
              </Grid>
              <Grid
                item
                sm={3}
                xs={12}
                sx={{
                  margin: "auto",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Button
                  color="primary"
                  variant="contained"
                  type="button"
                  className={classes.btnClass}
                  onClick={() => filterData()}
                >
                  <PersonSearch />
                </Button>
                <Button
                  color="primary"
                  sx={{ marginLeft: 1 }}
                  variant="contained"
                  type="button"
                  className={classes.btnClass}
                  onClick={() => {
                    setSearchData(filterInitialValues);
                    loadListData();
                  }}
                >
                  <Refresh />
                </Button>
                <Button
                  color="primary"
                  sx={{ marginLeft: 1 }}
                  variant="contained"
                  type="button"
                  className={classes.btnClass}
                  onClick={handleExportBulkInviteList}
                  title="Download Excel"
                >
                  <SimCardDownloadRounded />
                </Button>
              </Grid>
            </Grid>
          </CommonAccordion>
          <Card>
            <CardBody className={classes.cardbody}>
              <div>
                <CommonTableApBulkInvite
                  tableData={data}
                  tableHeaders={vendorBulkInviteListTableHeader}
                  tableColumns={tableColumns}
                  rowsPerPageOptions={[25, 50, 100]}
                  count={totalRecord}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  displayPagination={true}
                />
              </div>
            </CardBody>
          </Card>
        </Row>
      </Container>
      <ToastContainer autoClose={1000} />
    </Fragment>
  );
};

export default VendorBulkInviteList;
